import { css } from "@emotion/css";
import { CircularProgress } from "@octopusdeploy/design-system-components";
import { ExclamationTriangleIcon, CheckCircleIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDGatewayHealthStatus } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
type GatewayDiscoveryProgressProps = {
    name: string;
    isFound: boolean;
    bearerTokenExpired?: boolean;
    gatewayHealthStatus: ArgoCDGatewayHealthStatus;
};
export const GatewayDiscoveryProgress = (props: GatewayDiscoveryProgressProps) => {
    const registrationContent = useMemo(() => {
        if (props.isFound) {
            return (<>
                    <div className={styles.iconContainer}>
                        <CheckCircleIcon size={20} color={themeTokens.color.icon.success}/>
                    </div>
                    <span>
                        <b>{props.name}</b> established a connection
                    </span>
                </>);
        }
        else if (props.bearerTokenExpired) {
            return (<>
                    <div className={styles.iconContainer}>
                        <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.danger}/>
                    </div>
                    <span>
                        <strong>Your bearer token expired</strong> before the agent could establish a connection. To try again, add another Argo CD Gateway.
                    </span>
                </>);
        }
        else {
            return (<>
                    <div className={styles.iconContainer}>
                        <CircularProgress size="container" aria-hidden="true"/>
                    </div>
                    <span>Waiting for the gateway to connect</span>
                </>);
        }
    }, [props.bearerTokenExpired, props.isFound, props.name]);
    const healthCheckContent = useMemo(() => {
        switch (props.gatewayHealthStatus) {
            case "Unknown":
                return props.isFound ? (<>
                        <div className={styles.iconContainer}>
                            <CircularProgress size="container" aria-hidden="true"/>
                        </div>
                        <span>
                            <span>Performing health check...</span>
                        </span>
                    </>) : null;
            case "Healthy":
                return (<>
                        <div className={styles.iconContainer}>
                            <CheckCircleIcon size={20} color={themeTokens.color.icon.success}/>
                        </div>
                        <span>
                            <b>{props.name}</b> is healthy
                        </span>
                    </>);
            case "ArgoCDConnectionIssues":
                return (<>
                        <div className={styles.iconContainer}>
                            <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.warning}/>
                        </div>
                        <span>
                            <b>{props.name}</b> has issues connecting to the target Argo CD instance
                        </span>
                    </>);
            case "Unavailable":
                return (<>
                        <div className={styles.iconContainer}>
                            <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.danger}/>
                        </div>
                        <span>
                            <b>{props.name}</b> health check failed
                        </span>
                    </>);
        }
    }, [props.gatewayHealthStatus, props.isFound, props.name]);
    return (<div>
            <div className={styles.contentContainer}>{registrationContent}</div>
            {healthCheckContent && <div className={styles.contentContainer}>{healthCheckContent}</div>}
        </div>);
};
const styles = {
    contentContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
        "&:not(:last-child)": {
            marginBottom: space[16],
        },
    }),
    iconContainer: css({
        padding: `${space[4]} ${space[2]} 0 ${space[2]}`,
        marginRight: space[4],
        width: "24px",
    }),
};
